import {fmt, fmt0, qtyNum} from './format';
import {deliverableQty, groupOf, isExcluded} from './availability';

// Rabattplan je Gruppe (Original `discountPlan`, Zeile 1059) und das
// Vertriebsweg-Gate (`effectiveChannel`/`staffelAllowed`, 1050–1053).
// In Stufe 0 wird der Plan nur angezeigt (Ticker, Staffel-Hinweis je Zeile);
// angewendet wird er erst mit dem Rabatte-Schritt.

/** Aktiver Vertriebsweg des Belegs. */
export function effectiveChannel(order) {
    return (order && order.salesChannel) || null;
}

/** Gilt in diesem Vertriebsweg überhaupt ein Staffelrabatt? */
export function staffelAllowed(order, stock) {
    const channels = stock.staffelChannels || [];
    return !channels.length || channels.includes(effectiveChannel(order));
}

/** Anzeigename eines Vertriebswegs. */
export function channelLabelOf(code, order) {
    const c = ((order && order.channels) || []).find((x) => x.code === code);
    return c ? c.label : (code || '—');
}

/**
 * Warenwert, Stückzahl, erreichte und nächste Stufe je Rabattgruppe.
 *
 * @param {object[]} rows
 * @param {object} stock
 * @param {object} order
 * @param {(r: object) => number} [qtyFn] - Vorgabe: lieferbare Menge.
 *        Der Ticker übergibt bewusst die Bestellmenge (Leitzahl).
 */
export function discountPlan(rows, stock, order, qtyFn) {
    const qty = qtyFn || ((r) => deliverableQty(r, stock, order));
    const sums = {};
    const grpRows = {};
    const qtys = {};

    for (const r of rows) {
        if (r.del || r.locked) {
            continue;
        }
        const g = groupOf(r, stock);
        if (g === 'none') {
            continue;
        }
        const dq = qty(r);
        sums[g] = (sums[g] || 0) + (parseFloat(r.unitPrice) || 0) * dq;
        qtys[g] = (qtys[g] || 0) + dq;
        (grpRows[g] = grpRows[g] || []).push(r);
    }

    const chanOk = staffelAllowed(order, stock);

    return (stock.rules || []).map((g) => {
        const sum = sums[g.key] || 0;
        const groupRows = grpRows[g.key] || [];
        const groupQty = qtys[g.key] || 0;
        const hasTiers = (g.tiers || []).length > 0;
        let pct = null;
        let tier = null;
        let next = null;

        for (const [v, p] of (g.tiers || [])) {
            if (sum >= v) {
                pct = p;
                tier = v;
            } else if (next === null) {
                next = [v, p];
            }
        }

        // Staffelrabatt gilt nur in den freigegebenen Vertriebswegen.
        const channelBlocked = hasTiers && !chanOk;
        if (channelBlocked) {
            pct = null;
            tier = null;
        }

        const toChange = pct === null
            ? 0
            : groupRows.filter((r) => String(r.discountPercentage || '0') !== String(pct)).length;
        const palletHit = !!(g.channel && g.channelMinQty && groupQty >= g.channelMinQty);

        return {
            key: g.key,
            label: g.label,
            sum,
            qty: groupQty,
            n: groupRows.length,
            pct,
            tier,
            next,
            hasTiers,
            tiers: g.tiers || [],
            rows: groupRows,
            toChange,
            channelBlocked,
            channel: g.channel || null,
            channelLabel: g.channelLabel || '',
            channelMinQty: g.channelMinQty || null,
            palletHit,
        };
    }).filter((p) => p.n > 0);
}

/** Rabattplan nach Gruppenschlüssel — Grundlage des Staffel-Hinweises je Zeile. */
export function planByGroup(rows, stock, order) {
    const out = {};
    if (!stock.groups) {
        return out;
    }
    for (const p of discountPlan(rows, stock, order)) {
        out[p.key] = p;
    }
    return out;
}

/** Bestellmenge als Leitzahl des Tickers. */
export function orderedQty(r) {
    return qtyNum(r.quantity);
}

// ── Anwenden (Stufe 2) ──────────────────────────────────────────────────────
// Ab hier wird der Rabattplan nicht nur angezeigt, sondern gesetzt. Alle
// Funktionen sind rein: sie bekommen die Arbeitsliste und geben eine neue
// zurück. Geschrieben wird ausschließlich in die Tabelle (blau markiert) —
// nach weclapp geht das erst mit „Speichern".

/**
 * Masterbox-Prozentsatz für EINEN Artikel (Original `masterboxPctFor`, 1247).
 *
 * Quelle ist vorrangig das Artikelfeld „Masterbox Rabatt" aus weclapp
 * (`masterboxDiscount`). Ist es gefüllt (> 0), gilt dieser Satz; steht dort
 * ausdrücklich 0, gibt es keinen Rabatt. Ist es leer, greift die
 * Übergangsregel: Artikelnummer beginnt mit „9000" (Caps) → `capsPercent`,
 * sonst Rabattgruppe Pumpmarker → `pumpmarkerPercent`, sonst kein Rabatt.
 *
 * Gegenüber dem Original zusätzlich: Die Übergangsregel lässt sich auf der
 * Pflegeseite abschalten (`transitionalRuleActive`), sobald das weclapp-Feld
 * flächig gepflegt ist (ABGLEICH J20/O9). Sie ist ausdrücklich befristet.
 *
 * @returns {{pct: number, src: 'feld'|'feld0'|'caps'|'pumpmarker'|'none'}}
 */
export function masterboxPctFor(r, stock) {
    const regel = stock.masterboxRule || {};
    const feld = (stock.masterboxDiscount || {})[String(r.articleId)];

    if (feld != null && feld > 0) {
        return {pct: Number(feld), src: 'feld'};
    }
    if (feld != null && Number(feld) === 0) {
        // Ausdrücklich 0 = kein Masterbox-Rabatt für diesen Artikel.
        return {pct: 0, src: 'feld0'};
    }
    if (regel.transitionalRuleActive === false) {
        return {pct: 0, src: 'none'};
    }

    const num = String(r.articleNumber || '');
    if (num.startsWith('9000')) {
        return {pct: Number(regel.capsPercent != null ? regel.capsPercent : 5), src: 'caps'};
    }
    if (groupOf(r, stock) === 'pumpmarker') {
        return {pct: Number(regel.pumpmarkerPercent != null ? regel.pumpmarkerPercent : 7.5), src: 'pumpmarker'};
    }
    return {pct: 0, src: 'none'};
}

/**
 * Masterbox-Plan je Position (Original `masterboxPlan`, 1270).
 *
 * Der Rabatt gilt nur auf die Stück in vollen Boxen
 * (`floor(Menge / Boxgröße) × Boxgröße`), der Rest bleibt ohne. Er gilt für
 * jeden Kunden, unabhängig vom Vertriebsweg (ABGLEICH J17/O10).
 *
 * @returns {{row, mb, qty, full, remainder, pct, src, done, needsSplit}[]}
 */
export function masterboxPlan(rows, stock) {
    if (!stock.masterboxRule) {
        return [];
    }

    const out = [];

    for (const r of (rows || [])) {
        // Rest-Zeilen (`_new`) nicht erneut teilen. Positionen ohne Lieferung
        // („Keine") bleiben außen vor — was nicht geliefert wird, bekommt auch
        // keinen Rabatt und wird erst recht nicht geteilt.
        if (r.del || r.locked || r._new || isExcluded(r)) {
            continue;
        }
        const mb = (stock.masterbox || {})[String(r.articleId)];
        if (!(mb > 0)) {
            continue;
        }
        const rate = masterboxPctFor(r, stock);
        const pct = rate.pct;
        if (!(pct > 0)) {
            continue;
        }
        const qty = qtyNum(r.quantity);
        if (qty < mb) {
            // Weniger als eine volle Masterbox — kein Rabatt.
            continue;
        }

        const full = Math.floor(qty / mb) * mb;
        const remainder = +(qty - full).toFixed(4);
        const cur = parseFloat(String(r.discountPercentage).replace(',', '.')) || 0;

        out.push({
            row: r,
            mb,
            qty,
            full,
            remainder,
            pct,
            src: rate.src,
            // Exaktes Vielfaches und Rabatt schon gesetzt.
            done: remainder === 0 && cur === pct,
            needsSplit: remainder > 0,
        });
    }

    return out;
}

/**
 * Masterbox-Rabatt anwenden (Original `applyMasterbox`, 1288): Rabatt setzen,
 * die Zeile auf die vollen Boxen kürzen und die Restmenge als neue Zeile mit
 * 0 % direkt darunter einfügen (`_new`, `_mbRest`, `_after`). Der Anker
 * `_after` sorgt dafür, dass die Rest-Zeile auch in weclapp unmittelbar unter
 * ihrer Ursprungszeile landet (ABGLEICH J22/C10).
 *
 * @param {() => string} nextKey - Schlüsselgeber für neue Zeilen (rows.js).
 * @returns {object[]} neue Arbeitsliste
 */
export function applyMasterbox(rows, stock, nextKey) {
    const plan = masterboxPlan(rows, stock).filter((p) => !p.done);
    if (!plan.length) {
        return rows;
    }

    const proZeile = new Map(plan.map((p) => [p.row, p]));
    const out = [];

    for (const r of rows) {
        const p = proZeile.get(r);
        if (!p) {
            out.push(r);
            continue;
        }

        const geaendert = {
            ...r,
            discountPercentage: String(p.pct),
            ...(p.needsSplit ? {quantity: String(p.full)} : {}),
        };
        out.push(geaendert);

        if (p.needsSplit) {
            out.push({
                id: null,
                articleId: r.articleId,
                articleNumber: r.articleNumber,
                title: r.title,
                quantity: String(p.remainder),
                unitPrice: r.unitPrice,
                shippedQuantity: '0',
                discountPercentage: '0',
                locked: false,
                del: false,
                _key: nextKey(),
                _new: true,
                _mbRest: true,
                _after: r.id,
                _stammOverride: r._stammOverride || null,
                // Bestand und Preis dieses Artikels sind schon geladen — der
                // Nachschlag für neue Zeilen kann entfallen.
                _detailStarted: true,
                _orig: JSON.stringify({quantity: String(p.remainder), discountPercentage: '0', stammOverride: null}),
            });
        }
    }

    return out;
}

/**
 * Vertriebsweg-Vorschlag des Palettenpreises (Original
 * `palletChannelSuggestion`, 1092): der erste Treffer mit erreichter
 * Mindestmenge und abweichendem Kanal gewinnt.
 */
export function palletChannelSuggestion(plan, order, pendingChannel) {
    const cur = pendingChannel || (order && order.salesChannel);
    return plan.find((p) => p.palletHit && p.channel && p.channel !== cur) || null;
}

/**
 * Kompakte Vorschau, was „% jetzt anwenden" täte (Original
 * `discountPreview`, 1098).
 *
 * @returns {string[]}
 */
export function discountPreview(rows, stock, order, pendingChannel) {
    const lines = [];

    for (const p of discountPlan(rows, stock, order)) {
        if (p.channel && p.palletHit) {
            if ((pendingChannel || (order && order.salesChannel)) !== p.channel) {
                lines.push(`${p.label} → ${p.channelLabel} (Palettenpreis)`);
            }
        } else if (p.pct !== null && p.toChange > 0) {
            lines.push(`${p.label} → ${p.pct} % (${p.toChange} Pos.)`);
        }
    }

    for (const m of masterboxPlan(rows, stock)) {
        if (!m.done) {
            lines.push(`Masterbox ${m.row.articleNumber}: ${fmt0(m.full)}× @ ${fmt(m.pct)} %`
                + (m.needsSplit ? ' + Rest' : ''));
        }
    }

    return lines;
}

/**
 * Alle Rabatte auf einmal setzen (Original `applyDiscountsDirect`, 1337):
 * Staffelsätze je Gruppe, Palettenpreis als vorgemerkter Vertriebsweg und der
 * Masterbox-Rabatt samt Teilung.
 *
 * @returns {{rows: object[], channel: ?string}} `channel` = neu vorzumerkender
 *          Vertriebsweg, `null` wenn unverändert.
 */
export function applyDiscounts(rows, stock, order, pendingChannel, nextKey) {
    const plan = discountPlan(rows, stock, order);
    const saetze = new Map();

    for (const p of plan) {
        if (p.pct === null) {
            continue;
        }
        for (const r of p.rows) {
            saetze.set(r, String(p.pct));
        }
    }

    const gesetzt = rows.map((r) => (saetze.has(r) ? {...r, discountPercentage: saetze.get(r)} : r));
    const sug = palletChannelSuggestion(plan, order, pendingChannel);

    return {
        rows: applyMasterbox(gesetzt, stock, nextKey),
        channel: sug ? sug.channel : null,
    };
}

/**
 * Einen einzelnen Rabatt anwenden (Original `applyDiscountGroup`, 1350):
 * Staffelsatz einer Gruppe, Palettenpreis oder — mit dem Schlüssel `__mb__` —
 * der Masterbox-Rabatt.
 *
 * @returns {{rows: object[], channel: ?string, text: ?string}} `text` ist die
 *          Meldung für die Sprechblase unten rechts.
 */
export function applyDiscountGroup(rows, stock, order, key, pendingChannel, nextKey) {
    if (key === '__mb__') {
        return {
            rows: applyMasterbox(rows, stock, nextKey),
            channel: null,
            text: '✓ Masterbox-Rabatt in der Tabelle gesetzt',
        };
    }

    const p = discountPlan(rows, stock, order).find((x) => x.key === key);
    if (!p) {
        return {rows, channel: null, text: null};
    }

    if (p.channel && p.palletHit) {
        return {rows, channel: p.channel, text: `✓ Palettenpreis vorgemerkt (${p.channelLabel})`};
    }

    if (p.pct !== null) {
        const betroffen = new Set(p.rows);
        return {
            rows: rows.map((r) => (betroffen.has(r) ? {...r, discountPercentage: String(p.pct)} : r)),
            channel: null,
            text: `✓ ${p.label}: ${p.pct} % gesetzt`,
        };
    }

    return {rows, channel: null, text: null};
}
